// src/services/realtimeService.js
import {
    collection,
    doc,
    query,
    onSnapshot,
    where,
    orderBy,
    limit,
} from 'firebase/firestore';
import firebaseConfigService from './firebaseConfig';

class RealtimeService {
    constructor() {
        this.firestore = null;
        this.listeners = new Map(); // To store unsubscribe functions
    }

    _getFirestore() {
        if (!this.firestore) {
            const { firestore } = firebaseConfigService.getServices();
            this.firestore = firestore;
            if (!this.firestore) {
                throw new Error("Firestore is not initialized. Call firebaseConfigService.initialize() first.");
            }
        }
        return this.firestore;
    }

    /**
     * Listens to a Firestore collection with optional query parameters.
     * @param {string} listenerKey A unique key to identify this listener (e.g., 'case-123-messages')
     * @param {string} collectionName The name of the collection to listen to.
     * @param {object} queryParams Optional. Contains 'where', 'orderBy', 'limit' arrays.
     * @param {function} onData Callback function for when new data arrives (receives array of documents).
     * @param {function} onError Callback function for when an error occurs.
     * @returns {function} An unsubscribe function to stop listening.
     */
    listenToCollection(listenerKey, collectionName, queryParams = {}, onData, onError) {
        const db = this._getFirestore();
        let q = collection(db, collectionName);

        // Apply where clauses
        if (queryParams.where && Array.isArray(queryParams.where)) {
            queryParams.where.forEach(condition => {
                q = query(q, where(condition.field, condition.operator, condition.value));
            });
        }

        // Apply orderBy clauses
        if (queryParams.orderBy && Array.isArray(queryParams.orderBy)) {
            queryParams.orderBy.forEach(order => {
                q = query(q, orderBy(order.field, order.direction));
            });
        }

        // Apply limit
        if (queryParams.limit) {
            q = query(q, limit(queryParams.limit));
        }

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const data = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                onData(data);
            },
            (error) => {
                console.error(`RealtimeService: Error listening to collection ${collectionName} for key ${listenerKey}:`, error);
                onError(error);
            }
        );

        // Store the unsubscribe function
        this.listeners.set(listenerKey, unsubscribe);
        console.log(`RealtimeService: Listener set up for key: ${listenerKey}`);
        return unsubscribe;
    }

    /**
     * Listens to a single Firestore document.
     * @param {string} listenerKey A unique key to identify this listener (e.g., 'call-123')
     * @param {string} collectionName The name of the collection containing the document.
     * @param {string} documentId The ID of the document to listen to.
     * @param {function} onData Callback function for when document data changes (receives document data or null if not exists).
     * @param {function} onError Callback function for when an error occurs (optional).
     * @returns {function} An unsubscribe function to stop listening.
     */
    listenToDocument(listenerKey, collectionName, documentId, onData, onError) {
        const db = this._getFirestore();
        const docRef = doc(db, collectionName, documentId);

        const unsubscribe = onSnapshot(
            docRef,
            (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const data = {
                        id: docSnapshot.id,
                        ...docSnapshot.data(),
                    };
                    onData(data);
                } else {
                    console.warn(`RealtimeService: Document ${documentId} does not exist in ${collectionName}`);
                    onData(null);
                }
            },
            (error) => {
                console.error(`RealtimeService: Error listening to document ${documentId} in ${collectionName} for key ${listenerKey}:`, error);
                if (onError) {
                    onError(error);
                }
            }
        );

        // Store the unsubscribe function
        this.listeners.set(listenerKey, unsubscribe);
        console.log(`RealtimeService: Document listener set up for key: ${listenerKey}`);
        return unsubscribe;
    }

    /**
     * Unsubscribes a specific listener using its key.
     * @param {string} listenerKey The unique key of the listener to unsubscribe.
     */
    unsubscribeListener(listenerKey) {
        if (this.listeners.has(listenerKey)) {
            this.listeners.get(listenerKey)(); // Call the unsubscribe function
            this.listeners.delete(listenerKey);
            console.log(`RealtimeService: Listener unsubscribed for key: ${listenerKey}`);
        } else {
            console.warn(`RealtimeService: No listener found for key: ${listenerKey}`);
        }
    }

    /**
     * Unsubscribe all active listeners.
     */
    unsubscribeAllListeners() {
        this.listeners.forEach((unsubscribe, key) => {
            unsubscribe();
            console.log(`RealtimeService: Listener unsubscribed for key: ${key}`);
        });
        this.listeners.clear();
    }
}

const realtimeService = new RealtimeService();
export default realtimeService;